import React, { useState, useEffect, useContext } from "react";

import {
  Box,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Typography,
  Stack,
  TextField,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import { isEmpty } from "lodash";
import moment from "moment";

import { getToken } from "../../api/Account";
import { simpleMessage } from "../../helpers/Helpers";
import { DataContext } from "../../context/DataContext";
import NoData from "../../components/NoData";
import { getGastosAsync } from "../../api/Contabilidad";
import ListFooter from "../../components/ui/ListFooter";
import SegmentedTabs from "../../components/ui/SegmentedTabs";
import { monoFont } from "../../theme";

// Los ids de tipo de gasto que espera el API son 1/2/0; las pestañas usan
// indice 0/1/2, por eso el mapeo explicito.
const TIPOS = [
  { label: "Administrativos", id: 1 },
  { label: "Operativos", id: 2 },
  { label: "Otros", id: 0 },
];

const money = (n) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(n);

const Gastos = () => {
  const [gastosList, setGastosList] = useState([]);
  const [tipoIndex, setTipoIndex] = useState(0);

  var date = new Date();
  const [fechaDesde, setFechaDesde] = useState(
    new Date(date.getFullYear(), date.getMonth(), 1)
  );
  const [fechaHasta, setFechaHasta] = useState(date);

  const [sumGastos, setSumGastos] = useState(0);

  const { setIsLoading, setIsUnautorized } = useContext(DataContext);

  const token = getToken();

  useEffect(() => {
    (async () => {
      var start = moment(fechaDesde);
      var end = moment(fechaHasta);
      if (moment.duration(start.diff(end)).asDays() > 0) {
        simpleMessage("Rango de fechas incorrecto", "error");
        setFechaDesde(new Date(date.getFullYear(), date.getMonth(), 1));
        return;
      }

      setIsLoading(true);
      const data = {
        idTipoGasto: TIPOS[tipoIndex].id,
        desde: moment(fechaDesde).format("YYYY-MM-DD").toString(),
        hasta: moment(fechaHasta).format("YYYY-MM-DD").toString(),
      };
      const result = await getGastosAsync(token, data);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setGastosList(result.data.result);
      let gastos = 0;
      result.data.result.forEach((item) => (gastos += item.entradas));
      setSumGastos(gastos);

      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta, tipoIndex]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = gastosList.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <SegmentedTabs
            tabs={TIPOS.map((tipo) => ({ label: tipo.label }))}
            value={tipoIndex}
            onChange={(index) => {
              setTipoIndex(index);
              setCurrentPage(1);
            }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DesktopDatePicker
              closeOnSelect
              label="Desde"
              inputFormat="DD/MM/YYYY"
              value={fechaDesde}
              onChange={(newValue) => setFechaDesde(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />

            <DesktopDatePicker
              closeOnSelect
              label="Hasta"
              inputFormat="DD/MM/YYYY"
              value={fechaHasta}
              onChange={(newValue) => setFechaHasta(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
          </Stack>
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Fecha</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="center">Fondos</TableCell>
                    <TableCell>Realizado por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {moment(row.fecha).format("L")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.description}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: monoFont, fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}
                      >
                        {money(row.entradas)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {row.payWith}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {`${(row.realizadoPor || "").split(" ")[0] || ""} ${
                          (row.realizadoPor || "").split(" ")[1] || ""
                        }`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                flexWrap: "wrap",
                padding: "16px 22px",
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "action.hover",
              }}
            >
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "text.secondary" }}>
                Total de gastos
              </Typography>
              <Typography
                sx={{
                  fontFamily: monoFont,
                  fontWeight: 700,
                  fontSize: 18,
                  color: "warning.main",
                }}
              >
                {money(sumGastos)}
              </Typography>
            </Box>

            <ListFooter
              data={gastosList}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="gastos"
            />
          </>
        )}
      </Paper>
    </div>
  );
};

export default Gastos;
